import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FaTimes, FaFilePdf, FaCheck, FaSpinner } from 'react-icons/fa';

const AnnualReportPDFPreviewModal = ({
  pdfDataUri,
  title = 'Review Your Checklist',
  subtitle = 'Please review the details below before final submission',
  backLabel = 'Back to Edit',
  confirmLabel = 'Confirm & Submit',
  onClose,
  onConfirm,
  isSubmitting = false,
}) => {
  const modalRoot = typeof document !== 'undefined' ? document.body : null;

  useEffect(() => {
    if (!modalRoot) return undefined;
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = overflow;
    };
  }, [modalRoot]);

  if (!modalRoot) return null;

  return createPortal(
    <div className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/80 backdrop-blur-md p-0 sm:p-4 animate-fade-in">
      <div className="bg-white dark:bg-dark-800 w-full h-full sm:h-[95vh] sm:max-w-5xl sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        <div className="p-4 sm:p-6 border-b border-gray-100 dark:border-dark-700 flex items-center justify-between bg-white dark:bg-dark-800">
          <div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">{title}</h3>
            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1">{subtitle}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-dark-700 text-gray-500 dark:text-gray-400 transition-colors disabled:opacity-50"
            aria-label="Close preview"
          >
            <FaTimes className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 bg-gray-100 dark:bg-dark-900 overflow-hidden relative">
          {pdfDataUri ? (
            <iframe
              src={`${pdfDataUri}#toolbar=0&navpanes=0&view=FitH`}
              className="w-full h-full border-none"
              title="Annual Report Checklist PDF Preview"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <div className="text-center">
                <FaSpinner className="w-8 h-8 animate-spin text-primary-500 mx-auto mb-2" />
                <p className="text-sm text-gray-500 dark:text-gray-400">Generating PDF preview...</p>
              </div>
            </div>
          )}
        </div>

        <div className="p-4 sm:p-6 border-t border-gray-100 dark:border-dark-700 bg-white dark:bg-dark-800 flex flex-col sm:flex-row items-center gap-3">
          <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 sm:flex-1 flex items-center gap-2">
            <FaFilePdf className="w-4 h-4 text-primary-500 shrink-0" />
            Review your checklist PDF, then confirm to submit.
          </p>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="w-full sm:w-auto px-8 py-3 rounded-xl font-semibold text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-dark-700 hover:bg-gray-200 dark:hover:bg-dark-600 transition-all disabled:opacity-50"
          >
            {backLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isSubmitting || !pdfDataUri}
            className="w-full sm:w-auto px-8 py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-primary-600 to-primary-700 shadow-lg shadow-primary-500/30 hover:shadow-xl transition-all flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <FaSpinner className="w-4 h-4 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <FaCheck className="w-4 h-4" />
                {confirmLabel}
              </>
            )}
          </button>
        </div>
      </div>
    </div>,
    modalRoot
  );
};

export default AnnualReportPDFPreviewModal;
